import { lazy, useContext, useEffect, useState } from 'react'
import { CheckBox, NVButton } from '@nv/basic-components'
import { AlertBanner, OnboardProgressBar, PhoneInput, useNVModal } from '@nv/components'
import { VoterOnboardingContext, useVoterUserContext } from '@nv/contexts'
import HandIcon from '@nv/images/design/hand-icon'
import InfoIcon from '@nv/images/design/info-icon'
import SecurityIcon from '@nv/images/design/security-icon'
import { OnlineVoterAuthService } from '@nv/services'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import styles from './OnBoardingAuthMethod.module.scss'
import 'react-phone-number-input/style.css'
import '@djthoms/pretty-checkbox'
const AuthAppInfo = lazy(() => import('@nv/lazy-loadable/lib/modals/AuthAppInfo'))

const initialValues = {
  phone: '',
}

export const OnBoardingAuthMethod = ({ hasPhoneNumber = false }: any) => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const navigate = useNavigate()
  const { setAuthMethod } = useContext(VoterOnboardingContext)
  const [activeSelection, setActiveSelection] = useState(null)
  const [notValid, setNotValid] = useState(true)
  const { voterUser, setVoterUser } = useVoterUserContext()
  const [errorSendSMS, setErrorSendSMS] = useState(false)
  const { NVModal, openModal } = useNVModal()

  const schema = Yup.object().shape({
    phone: Yup.string().when([], {
      is: () => hasPhoneNumber && activeSelection === 'SMS', // Only validate phone when SMS is selected
      then: Yup.string().required(t('form.validations.phone')),
      otherwise: Yup.string(), // No validation when AuthApp is selected
    }),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: async () => onClickNext(),
  })

  const onClickNext = async () => {
    try {
      setErrorSendSMS(false)
      const phoneNumber = formik.values.phone
      if (hasPhoneNumber && phoneNumber && phoneNumber.length) {
        // if they enter a phone number, save it, even if they are not using sms as 2fa right now
        await OnlineVoterAuthService.saveMobilePhone(phoneNumber)
        setVoterUser({ ...voterUser, phone: phoneNumber, phoneNumber })
      }
      if (activeSelection === 'AuthApp') {
        const sent = await OnlineVoterAuthService.getAuthAppQRCode()
        if (sent.success) navigate('/voting/voter-auth/auth-app-setup', { state: { qrCodeDataUrl: sent.qrCodeDataUrl } })
      } else if (activeSelection === 'Email') {
        const sent = await OnlineVoterAuthService.sendEmailCode()
        if (sent.success) navigate('/voting/voter-verify/verify')
      } else {
        const sent = await OnlineVoterAuthService.sendSMSCode(currentLanguage)
        if (sent.success) navigate('/voting/voter-verify/verify')
        else setErrorSendSMS(true)
      }
    } catch (error) {
      // console.log(error)
    }
  }

  const onChangeMethod = ({ method }) => {
    setActiveSelection(method)
    setAuthMethod(method)
    setErrorSendSMS(false)
  }

  useEffect(() => {
    setNotValid(!activeSelection)
  }, [activeSelection])

  return (
    <div className={styles['container']}>
      <NVModal>
        <AuthAppInfo />
      </NVModal>

      <div className={styles['form']}>
        <OnboardProgressBar currentStep={2} />
        <div className={styles['body']}>
          <div className={styles['body__username']}>
            <span className={styles['body__username__label']}>{t('auth.yourUsername.voter')}</span>
            <span className={styles['body__username__value']}>{voterUser?.username}</span>
          </div>

          <AlertBanner
            icon={<HandIcon style={{ width: 50, height: 50 }} />}
            text={t('auth.authMethod.username-banner')}
            color={'var(--tertiary-color)'}
          />

          <div className={styles['body__method']}>
            <span className={styles['body__method__label']}>{t('auth.authMethod.heading')}</span>
            <InfoIcon className={styles['body__method__info-icon']} onClick={() => openModal()} />
          </div>

          <CheckBox
            checked={activeSelection === 'AuthApp'}
            onChange={() => onChangeMethod({ method: 'AuthApp' })}
            label={t('auth.authMethod.checkbox.auth_app')}
          />
          <CheckBox
            checked={activeSelection === 'Email'}
            onChange={() => onChangeMethod({ method: 'Email' })}
            label={t('auth.authMethod.checkbox.email')}
          />
          <CheckBox
            checked={activeSelection === 'SMS'}
            onChange={() => onChangeMethod({ method: 'SMS' })}
            label={t('auth.authMethod.checkbox.sms')}
          />
          {hasPhoneNumber && (
            <div className={styles['body__phone']}>
              <PhoneInput formik={formik} field='phone' label={t('auth.authMethod.enterPhone')} />
              {errorSendSMS && (
                <div className={styles['phone__error']}>
                  <span role='alert'>{t('auth.login.errors.sendSMS')}</span>
                </div>
              )}
            </div>
          )}

          <AlertBanner icon={<SecurityIcon style={{ width: 50, height: 50 }} />} text={t('auth.authMethod.banner')} />
        </div>
        <div className={styles['footer']}>
          <NVButton text={t('buttons.next')} height={50} onClick={() => formik.handleSubmit()} disabled={notValid} />
        </div>
      </div>
    </div>
  )
}
